/**
 * Pure TEA update for debounced synopsis drafts.
 *
 * Shell: `useSynopsisDraft.ts` (identity watch + createTimeoutHandle).
 */
import { assertNever } from "../lib/assertNever";
import { isJust, isNothing, just, nothing, type Maybe } from "../lib/maybe";
import type { UpdateFn } from "../lib/tea";

export type SynopsisDraftModel = {
  readonly draft: string;
  readonly lastSeeded: string;
  readonly lastIdentity: Maybe<string>;
  readonly pendingIdentity: Maybe<string>;
  readonly hasSeeded: boolean;
};

export const initialSynopsisDraftModel: SynopsisDraftModel = {
  draft: "",
  lastSeeded: "",
  lastIdentity: nothing,
  pendingIdentity: nothing,
  hasSeeded: false,
};

export type SynopsisDraftMsg =
  | {
      readonly type: "IdentityOrSourceChanged";
      readonly identity: Maybe<string>;
      readonly sourceValue: string;
    }
  | { readonly type: "DraftChanged"; readonly draft: string }
  | {
      readonly type: "SaveTimerFired";
      readonly identity: string;
      readonly value: string;
    };

export type SynopsisDraftCmd =
  | {
      readonly kind: "scheduleSave";
      readonly identity: string;
      readonly value: string;
    }
  | { readonly kind: "clearSave" }
  | {
      readonly kind: "commit";
      readonly identity: string;
      readonly value: string;
    };

function sameIdentity(a: Maybe<string>, b: Maybe<string>): boolean {
  if (a.tag !== b.tag) return false;
  if (a.tag === "nothing") return true;
  return b.tag === "just" && a.value === b.value;
}

export function updateSynopsisDraft(
  model: SynopsisDraftModel,
  msg: SynopsisDraftMsg,
): readonly [SynopsisDraftModel, readonly SynopsisDraftCmd[]] {
  switch (msg.type) {
    case "IdentityOrSourceChanged": {
      const identityChanged =
        model.hasSeeded === false ||
        sameIdentity(msg.identity, model.lastIdentity) === false;
      if (identityChanged) {
        const draft = isNothing(msg.identity) ? "" : msg.sourceValue;
        return [
          {
            draft,
            lastSeeded: draft,
            lastIdentity: msg.identity,
            pendingIdentity: nothing,
            hasSeeded: true,
          },
          [],
        ];
      }
      if (
        isJust(msg.identity) &&
        model.draft === model.lastSeeded &&
        msg.sourceValue !== model.draft
      ) {
        return [
          {
            ...model,
            draft: msg.sourceValue,
            lastSeeded: msg.sourceValue,
          },
          [],
        ];
      }
      return [model, []];
    }
    case "DraftChanged": {
      const identityM = model.lastIdentity;
      if (isNothing(identityM)) {
        return [{ ...model, draft: msg.draft }, []];
      }
      const identity = identityM.value;
      if (msg.draft === model.lastSeeded) {
        if (
          isJust(model.pendingIdentity) &&
          sameIdentity(model.pendingIdentity, just(identity))
        ) {
          return [
            { ...model, draft: msg.draft, pendingIdentity: nothing },
            [{ kind: "clearSave" }],
          ];
        }
        return [{ ...model, draft: msg.draft }, []];
      }
      return [
        {
          ...model,
          draft: msg.draft,
          pendingIdentity: just(identity),
        },
        [{ kind: "scheduleSave", identity, value: msg.draft }],
      ];
    }
    case "SaveTimerFired": {
      const lastSeeded = sameIdentity(model.lastIdentity, just(msg.identity))
        ? msg.value
        : model.lastSeeded;
      return [
        {
          ...model,
          pendingIdentity: nothing,
          lastSeeded,
        },
        [{ kind: "commit", identity: msg.identity, value: msg.value }],
      ];
    }
    default:
      return assertNever(msg);
  }
}

export const updateSynopsisDraftFn: UpdateFn<
  SynopsisDraftModel,
  SynopsisDraftMsg,
  SynopsisDraftCmd
> = updateSynopsisDraft;
